// Procedural WebAudio synthesis for Lumen Abyss SFX.
// Bakes to AudioBuffers at init; no downloaded samples.

function offlineContext(durationSec: number): OfflineAudioContext {
  return new OfflineAudioContext(2, Math.ceil(48000 * durationSec), 48000);
}

function applyEnvelope(
  ctx: OfflineAudioContext,
  node: AudioNode,
  attackMs: number,
  decayMs: number,
  sustainLevel: number,
  releaseMs: number,
  peak = 1
): void {
  const t0 = ctx.currentTime;
  const attack = attackMs / 1000;
  const decay = decayMs / 1000;
  const release = releaseMs / 1000;
  const gain = ctx.createGain();
  node.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + attack);
  gain.gain.linearRampToValueAtTime(sustainLevel, t0 + attack + decay);
  gain.gain.setValueAtTime(sustainLevel, t0 + attack + decay + 0.001);
  gain.gain.linearRampToValueAtTime(0, t0 + attack + decay + 0.001 + release);
}

function makeWhiteNoise(ctx: OfflineAudioContext, durationSec: number): AudioBuffer {
  const frames = Math.ceil(ctx.sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function makeBrownNoise(ctx: OfflineAudioContext, durationSec: number): AudioBuffer {
  const frames = Math.ceil(ctx.sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < frames; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + white * 0.02) / 1.02;
    data[i] = last * 3.5;
  }
  return buffer;
}

export async function createLanceFireBuffer(): Promise<AudioBuffer> {
  const duration = 0.12;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(660, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.02);
  applyEnvelope(ctx, osc, 2, 78, 0, 0, 0.9);
  osc.start();
  osc.stop(ctx.currentTime + duration);

  const partial = ctx.createOscillator();
  partial.type = 'sine';
  partial.frequency.setValueAtTime(1980, ctx.currentTime);
  applyEnvelope(ctx, partial, 2, 78, 0, 0, 0.15);
  partial.start();
  partial.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

export async function createLanceHitBuffer(): Promise<AudioBuffer> {
  const duration = 0.08;
  const ctx = offlineContext(duration);
  const noise = ctx.createBufferSource();
  noise.buffer = makeWhiteNoise(ctx, duration);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 900;
  noise.connect(filter);
  applyEnvelope(ctx, filter, 1, 50, 0, 0, 0.7);
  noise.start();

  const sine = ctx.createOscillator();
  sine.frequency.setValueAtTime(150, ctx.currentTime);
  applyEnvelope(ctx, sine, 1, 40, 0, 0, 0.5);
  sine.start();
  sine.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

// Manual laser cannon: brighter and sharper than the lance — a fast
// high-frequency downward chirp with a hot upper partial.
export async function createLaserFireBuffer(): Promise<AudioBuffer> {
  const duration = 0.1;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(2400, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(720, ctx.currentTime + 0.08);
  applyEnvelope(ctx, osc, 1, 60, 0, 10, 0.55);
  osc.start();
  osc.stop(ctx.currentTime + duration);

  const partial = ctx.createOscillator();
  partial.type = 'sine';
  partial.frequency.setValueAtTime(3600, ctx.currentTime);
  partial.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.05);
  applyEnvelope(ctx, partial, 1, 45, 0, 0, 0.2);
  partial.start();
  partial.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

// Laser impact: short bright band-passed noise snap with a high ping,
// versus the lance's darker low-passed thud.
export async function createLaserHitBuffer(): Promise<AudioBuffer> {
  const duration = 0.07;
  const ctx = offlineContext(duration);
  const noise = ctx.createBufferSource();
  noise.buffer = makeWhiteNoise(ctx, duration);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 3200;
  filter.Q.value = 1.5;
  noise.connect(filter);
  applyEnvelope(ctx, filter, 1, 40, 0, 0, 0.6);
  noise.start();

  const ping = ctx.createOscillator();
  ping.type = 'sine';
  ping.frequency.setValueAtTime(1800, ctx.currentTime);
  ping.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.05);
  applyEnvelope(ctx, ping, 1, 35, 0, 0, 0.35);
  ping.start();
  ping.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

export async function createEnemyDeathBuffer(): Promise<AudioBuffer> {
  // Tetress-informed kill sound (see tetress SOUND_SYSTEM.md §Beam Sound
  // Design): triangle blip in the 400-600 Hz band, ~90 ms, subtle — the
  // old sine-sub + highpassed noise read harsh at kill-rate volume.
  const duration = 0.12;
  const ctx = offlineContext(duration);
  // Soft triangle pop: 520 → 280 Hz, quick exponential-feeling decay.
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(520, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(280, ctx.currentTime + 0.09);
  applyEnvelope(ctx, osc, 2, 88, 0, 0, 0.5);
  osc.start();
  osc.stop(ctx.currentTime + duration);
  // Quiet sparkle partial an octave up: the "pleasant" tick on top.
  const sparkle = ctx.createOscillator();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(1040, ctx.currentTime);
  applyEnvelope(ctx, sparkle, 1, 40, 0, 0, 0.12);
  sparkle.start();
  sparkle.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

export async function createPlayerHitBuffer(): Promise<AudioBuffer> {
  const duration = 0.12;
  const ctx = offlineContext(duration);
  const noise = ctx.createBufferSource();
  noise.buffer = makeWhiteNoise(ctx, duration);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  noise.connect(filter);
  applyEnvelope(ctx, filter, 2, 80, 0, 0, 0.9);
  noise.start();

  const sine = ctx.createOscillator();
  sine.frequency.setValueAtTime(90, ctx.currentTime);
  applyEnvelope(ctx, sine, 2, 70, 0, 0, 0.6);
  sine.start();
  sine.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

export async function createDashBuffer(): Promise<AudioBuffer> {
  const duration = 0.3;
  const ctx = offlineContext(duration);
  const noise = ctx.createBufferSource();
  noise.buffer = makeWhiteNoise(ctx, duration);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(300, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.25);
  filter.Q.value = 2;
  noise.connect(filter);
  applyEnvelope(ctx, filter, 5, 120, 0, 0, 0.7);
  noise.start();

  const gulp = ctx.createOscillator();
  gulp.type = 'sine';
  gulp.frequency.setValueAtTime(70, ctx.currentTime);
  applyEnvelope(ctx, gulp, 5, 120, 0, 0, 0.35);
  gulp.start();
  gulp.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

export async function createPickupXpBuffer(): Promise<AudioBuffer> {
  const duration = 0.18;
  const ctx = offlineContext(duration);
  const note1 = ctx.createOscillator();
  note1.type = 'sine';
  note1.frequency.setValueAtTime(880, ctx.currentTime);
  applyEnvelope(ctx, note1, 2, 58, 0, 0, 0.35);
  note1.start();
  note1.stop(ctx.currentTime + 0.06);

  const note2 = ctx.createOscillator();
  note2.type = 'sine';
  note2.frequency.setValueAtTime(1320, ctx.currentTime + 0.06);
  applyEnvelope(ctx, note2, 2, 58, 0, 0, 0.35);
  note2.start(ctx.currentTime + 0.06);
  note2.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

// Short "music start" chime: ascending Lydian figure (root – #4 – octave),
// played once when the streaming soundtrack kicks in for a run.
export async function createMusicStartBuffer(): Promise<AudioBuffer> {
  const duration = 0.6;
  const ctx = offlineContext(duration);
  const midis = [74, 80, 86]; // D5, G#5 (the Lydian #4), D6
  midis.forEach((midi, i) => {
    const t0 = ctx.currentTime + i * 0.07;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440 * Math.pow(2, (midi - 69) / 12), t0);
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.22, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
    osc.start(t0);
    osc.stop(t0 + 0.4);
  });
  return ctx.startRendering();
}

export type CueId =
  | 'audio.weapons.lance-fire'
  | 'audio.weapons.lance-hit'
  | 'audio.weapons.laser-fire'
  | 'audio.weapons.laser-hit'
  | 'audio.enemy.death-standard'
  | 'audio.player.hit'
  | 'audio.player.dash'
  | 'audio.pickup.xp'
  | 'audio.ui.click'
  | 'audio.ui.confirm'
  | 'audio.ui.back'
  | 'audio.wave.start'
  | 'audio.wave.clear'
  | 'audio.wave.boss'
  | 'audio.music.start';

export async function createUiClickBuffer(): Promise<AudioBuffer> {
  const duration = 0.05;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(2000, ctx.currentTime);
  applyEnvelope(ctx, osc, 1, 12, 0, 0, 0.4);
  osc.start();
  osc.stop(ctx.currentTime + duration);
  return ctx.startRendering();
}

// Wave assault start: a bright rising fifth — a call to arms that reads over
// combat without clashing with the weapon fire cues.
export async function createWaveStartBuffer(): Promise<AudioBuffer> {
  const duration = 0.3;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(523, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(784, ctx.currentTime + 0.12);
  applyEnvelope(ctx, osc, 2, 160, 0, 90, 0.3);
  osc.start();
  osc.stop(ctx.currentTime + duration);

  const partial = ctx.createOscillator();
  partial.type = 'sine';
  partial.frequency.setValueAtTime(1568, ctx.currentTime);
  applyEnvelope(ctx, partial, 2, 140, 0, 90, 0.1);
  partial.start();
  partial.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

// Wave cleared: a soft major chime (root + fifth) — a breath, not a fanfare.
export async function createWaveClearBuffer(): Promise<AudioBuffer> {
  const duration = 0.5;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, ctx.currentTime);
  applyEnvelope(ctx, osc, 12, 320, 0, 160, 0.25);
  osc.start();
  osc.stop(ctx.currentTime + duration);

  const fifth = ctx.createOscillator();
  fifth.type = 'sine';
  fifth.frequency.setValueAtTime(1320, ctx.currentTime);
  applyEnvelope(ctx, fifth, 12, 300, 0, 160, 0.12);
  fifth.start();
  fifth.stop(ctx.currentTime + duration);

  return ctx.startRendering();
}

// Boss wave: a low sawtooth drone-drop over rumbling brown noise.
export async function createWaveBossBuffer(): Promise<AudioBuffer> {
  const duration = 0.7;
  const ctx = offlineContext(duration);
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(110, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(55, ctx.currentTime + 0.5);
  applyEnvelope(ctx, osc, 4, 400, 0, 200, 0.4);
  osc.start();
  osc.stop(ctx.currentTime + duration);

  const noise = ctx.createBufferSource();
  noise.buffer = makeBrownNoise(ctx, duration);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 300;
  noise.connect(filter);
  applyEnvelope(ctx, filter, 4, 350, 0, 200, 0.3);
  noise.start();

  return ctx.startRendering();
}

export const CUE_FACTORIES: Record<CueId, () => Promise<AudioBuffer>> = {
  'audio.weapons.lance-fire': createLanceFireBuffer,
  'audio.weapons.lance-hit': createLanceHitBuffer,
  'audio.weapons.laser-fire': createLaserFireBuffer,
  'audio.weapons.laser-hit': createLaserHitBuffer,
  'audio.enemy.death-standard': createEnemyDeathBuffer,
  'audio.player.hit': createPlayerHitBuffer,
  'audio.player.dash': createDashBuffer,
  'audio.pickup.xp': createPickupXpBuffer,
  'audio.ui.click': createUiClickBuffer,
  'audio.ui.confirm': createUiClickBuffer,
  'audio.ui.back': createUiClickBuffer,
  'audio.wave.start': createWaveStartBuffer,
  'audio.wave.clear': createWaveClearBuffer,
  'audio.wave.boss': createWaveBossBuffer,
  'audio.music.start': createMusicStartBuffer
};
